import { Button } from "@mui/material";
import I18n from "i18next";
import { parse } from "json2csv";
import uniq from "lodash/uniq";
import { useTranslation } from "react-i18next";
import { compareTwoPanelNumbers } from "../hvSubstations/PanelNumberOrdering";

import { MeasurementComponent, MeasurementType, SubLocation } from "../shared/enums/Result";
import { CableNaming, UTP2Conditions, UTP2Groups, UTP2Substations } from "../shared/enums/UTP2";
import { getMeasurementsByMeasurementType } from "../shared/helpers/ResultHelpers";
import { getOverallRAGStatus, getTranslatedSubLocationName } from "../shared/helpers/SubLocationHelpers";
import {
  CablePDMeasurement,
  Measurement,
  PanelMeasurement,
  Result,
  TEVMeasurement,
  UHFMeasurement,
  UltraSonicMeasurement,
  VPISMeasurement,
} from "../shared/interfaces/Result";

interface Props {
  result: Result;
}

export function ExportResultCSVButton(props: Props) {
  const { t } = useTranslation();

  function handleExportButtonClick() {
    const exportArray = buildCSVExport(props.result);
    const csvValue = parse(exportArray, {
      includeEmptyRows: true,
      header: false,
    });
    downloadCSVFile(
      csvValue,
      `${props.result.dataValue.MetaData.SubstationGroup?.SubstationName}-${t("common.formattedDate", { dateString: props.result.time })}.csv`,
    );
  }

  return (
    <Button variant="contained" onClick={handleExportButtonClick}>
      {t("results.exportCSV")}
    </Button>
  );
}

function downloadCSVFile(csv: string, fileName: string) {
  const file = encodeURI(csv);
  const a = document.createElement("a");

  a.href = `data:text/csv;charset=utf-8,${file}`;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
}

export function buildCSVExport(result: Result) {
  const exportArray: unknown[][] = [];

  const {
    Instrument: instrumentMetadata,
    SubstationGroup: substationMetadata,
    SubstationConditionsGroup: substationConditionsMetadata,
    SwitchGearGroup: switchgearMetadata,
    JobGroup: jobMetadata,
  } = result.dataValue.MetaData;

  const panelMeasurements = result.dataValue.PanelMeasurements.sort((a, b) =>
    compareTwoPanelNumbers(a.PanelNo, b.PanelNo, "asc"),
  );

  exportArray.push([I18n.t("substations.substationName"), substationMetadata?.SubstationName]);
  exportArray.push([I18n.t("results.surveyTime"), I18n.t("common.formattedDate", { dateString: result.time })]);

  exportArray.push([I18n.t("UTP2.calibrationDue"), instrumentMetadata.$CAL_DUE]);
  exportArray.push([I18n.t("UTP2.dna"), instrumentMetadata.$DNA]);
  exportArray.push([I18n.t("UTP2.firmwareVersion"), instrumentMetadata.$FIRMWARE_VERSION]);
  exportArray.push([I18n.t("UTP2.hardwareVersion"), instrumentMetadata.$HARDWARE_VERSION]);
  exportArray.push([I18n.t("UTP2.selfCheckResult"), instrumentMetadata.$SELF_CHECK]);
  exportArray.push([I18n.t("UTP2.serialNumber"), instrumentMetadata.$SERIAL]);

  const panelNumbers = [
    I18n.t("results.panelNumber", { val: "panels.panel" }),
    ...panelMeasurements.map((panelMeasurement) => panelMeasurement.PanelNo),
  ];
  const panelNames = [
    I18n.t("results.panelName", { val: "panels.panel" }),
    ...panelMeasurements.map((panelMeasurement) => panelMeasurement.PanelName),
  ];
  const panelChildlocations = buildPanelChildLocationsList(substationMetadata.CableNamingScheme);

  //Switch position
  exportArray.push([]); // blank line
  exportArray.push(panelNumbers);
  exportArray.push(panelNames);

  const statuses = getOverallRAGStatuses(panelMeasurements);

  exportArray.push([I18n.t("common.status"), ...statuses]);

  const switchPositions = panelMeasurements.map((panelMeasurement) => {
    let measurementSwitchPositions = Object.values(MeasurementType)
      .map((measurementType) => {
        const measurementsByType = getMeasurementsByMeasurementType(panelMeasurement, measurementType);
        if (measurementsByType?.length > 0) return measurementsByType[0].SwitchPosition;
        else return undefined;
      })
      .filter(Boolean);

    return uniq(measurementSwitchPositions)
      .map((switchPosition) => I18n.t(`UTP2.switchPosition.${switchPosition}`))
      .join("/");
  });

  exportArray.push([I18n.t("results.switchPosition"), ...switchPositions]);

  exportArray.push(
    ...buildMeasurementExportSection<TEVMeasurement>(
      MeasurementType.TEV,
      "MeasureDB",
      panelMeasurements,
      panelNumbers,
      panelNames,
      panelChildlocations,
    ),
  );
  exportArray.push(
    ...buildMeasurementExportSection<TEVMeasurement>(
      MeasurementType.TEV,
      "MeasurePPC",
      panelMeasurements,
      panelNumbers,
      panelNames,
      panelChildlocations,
    ),
  );
  exportArray.push(
    ...buildMeasurementExportSection<UltraSonicMeasurement>(
      MeasurementType.Ultra,
      "MeasureDBUV",
      panelMeasurements,
      panelNumbers,
      panelNames,
      panelChildlocations,
    ),
  );
  exportArray.push(
    ...buildMeasurementExportSection<CablePDMeasurement>(
      MeasurementType.TEV,
      "MeasurePC",
      panelMeasurements,
      panelNumbers,
      panelNames,
      panelChildlocations,
    ),
  );
  exportArray.push(
    ...buildMeasurementExportSection<CablePDMeasurement>(
      MeasurementType.TEV,
      "MeasurePPC",
      panelMeasurements,
      panelNumbers,
      panelNames,
      panelChildlocations,
    ),
  );
  exportArray.push(
    ...buildMeasurementExportSection<UHFMeasurement>(
      MeasurementType.TEV,
      "MeasureDBM",
      panelMeasurements,
      panelNumbers,
      panelNames,
      panelChildlocations,
    ),
  );
  exportArray.push(
    ...buildMeasurementExportSection<VPISMeasurement>(
      MeasurementType.VPIS,
      "MeasureMV",
      panelMeasurements,
      panelNumbers,
      panelNames,
      panelChildlocations,
    ),
  );
  exportArray.push(
    ...buildMeasurementExportSection<VPISMeasurement>(
      MeasurementType.VPIS,
      "MeasurePPC",
      panelMeasurements,
      panelNumbers,
      panelNames,
      panelChildlocations,
    ),
  );

  exportArray.push([I18n.t("common.jobNumber"), jobMetadata.JobNo]);

  exportArray.push([I18n.t("common.engineerName"), jobMetadata.EngineerName]);
  exportArray.push([I18n.t("substations.substationName"), substationMetadata.SubstationName]);
  exportArray.push([
    I18n.t("substations.substationType"),
    I18n.t(`UTP2.substationType.${substationMetadata.SubstationType}`),
  ]);
  exportArray.push([
    I18n.t(`UTP2.${UTP2Groups.Substation}.${UTP2Substations.CableNaming}`),
    I18n.t(`UTP2.cableNaming.${substationMetadata.CableNamingScheme}`),
  ]);
  exportArray.push([I18n.t("Manufacturer"), switchgearMetadata.Manufacturer]);
  exportArray.push([I18n.t("Switchgear Type"), switchgearMetadata.Type]);
  exportArray.push([I18n.t("Installation Date"), switchgearMetadata.InstallDate]);
  exportArray.push([I18n.t("Rated Voltage"), switchgearMetadata.RatedVoltage]);
  exportArray.push([I18n.t("Busbar Insulation"), I18n.t(`UTP2.insulationType.${switchgearMetadata?.Insulation}`)]);

  exportArray.push([
    I18n.t(`UTP2.${UTP2Groups.Conditions}.${UTP2Conditions.BackMetalDbReading}`),
    substationConditionsMetadata?.BackMetalDbReading,
  ]);
  exportArray.push([
    I18n.t(`UTP2.${UTP2Groups.Conditions}.${UTP2Conditions.BackMetalPPCReading}`),
    substationConditionsMetadata?.BackMetalPPCReading,
  ]);
  exportArray.push([
    I18n.t(`UTP2.${UTP2Groups.Conditions}.${UTP2Conditions.BackAirDbReading}`),
    substationConditionsMetadata?.BackAirDbReading,
  ]);
  exportArray.push([
    I18n.t(`UTP2.${UTP2Groups.Conditions}.${UTP2Conditions.BackAirPPCReading}`),
    substationConditionsMetadata?.BackAirPPCReading,
  ]);
  exportArray.push([
    I18n.t(`UTP2.${UTP2Groups.Conditions}.${UTP2Conditions.TemperatureCelsius}`),
    substationConditionsMetadata?.TemperatureCelsius,
  ]);
  exportArray.push([
    I18n.t(`UTP2.${UTP2Groups.Conditions}.${UTP2Conditions.RelativeHumidity}`),
    substationConditionsMetadata?.RelativeHumidity,
  ]);

  return exportArray;
}

function getOverallRAGStatuses(panelMeasurements: PanelMeasurement[]) {
  const statusList = panelMeasurements.map((panelMeasurement) => {
    return getOverallRAGStatus(panelMeasurement);
  });

  return statusList;
}

function buildMeasurementExportSection<T extends Measurement>(
  measurementType: MeasurementType,
  measurementKey: keyof T,
  panelMeasurements: PanelMeasurement[],
  panelNumbers: string[],
  panelNames: string[],
  panelChildlocations: SubstationLocationDetails[],
) {
  let exportSection: unknown[][] = [];

  exportSection.push([]); // blank line

  const measurementLabel = `${I18n.t(`MeasurementTypes.${measurementType}`)} ${I18n.t(`measurements.short.${String(measurementKey)}`)}`;
  exportSection.push([measurementLabel]);
  exportSection.push(panelNumbers);
  exportSection.push(panelNames);

  const locationMeasurementValues = panelChildlocations.map((location) => {
    const panelMeasurementValues = panelMeasurements?.map((panelMeasurement) => {
      const measurementsByMeasurementTypeAndLocation = getMeasurementsByMeasurementType<T>(
        panelMeasurement,
        measurementType,
      )?.find(
        (measurement) =>
          measurement.ComponentType === location.component && measurement.SubLocation === location.subLocation,
      );

      return measurementsByMeasurementTypeAndLocation
        ? measurementsByMeasurementTypeAndLocation[measurementKey]
        : undefined;
    });

    return [location.label, ...panelMeasurementValues];
  });

  exportSection.push(...locationMeasurementValues);

  return exportSection;
}

function buildPanelChildLocationsList(cableNaming?: CableNaming) {
  const cables = [
    SubLocation.P1_C1,
    SubLocation.P1_C2,
    SubLocation.P1_C3,
    SubLocation.P1_C4,
    SubLocation.P2_C1,
    SubLocation.P2_C2,
    SubLocation.P2_C3,
    SubLocation.P2_C4,
    SubLocation.P3_C1,
    SubLocation.P3_C2,
    SubLocation.P3_C3,
    SubLocation.P3_C4,
  ].map((cableSublocation) =>
    buildSubstationLocationDetails(MeasurementComponent.Cables, cableSublocation, cableNaming),
  );

  const cableBox = [
    SubLocation.SUB_LOC_UPPER_LEFT,
    SubLocation.SUB_LOC_UPPER_CENTRE,
    SubLocation.SUB_LOC_UPPER_RIGHT,
    SubLocation.SUB_LOC_CENTRE_LEFT,
    SubLocation.SUB_LOC_CENTRE,
    SubLocation.SUB_LOC_CENTRE_RIGHT,
    SubLocation.SUB_LOC_LOWER_LEFT,
    SubLocation.SUB_LOC_LOWER_CENTRE,
    SubLocation.SUB_LOC_LOWER_RIGHT,
  ].map((cableBoxSublocation) => buildSubstationLocationDetails(MeasurementComponent.CableBox, cableBoxSublocation));

  const ctChamber = buildSubstationLocationDetails(MeasurementComponent.CTChamber);
  const vtChamber = buildSubstationLocationDetails(MeasurementComponent.VTChamber);
  const upperBusbars = buildSubstationLocationDetails(MeasurementComponent.UpperBusbars);
  const lowerBusbars = buildSubstationLocationDetails(MeasurementComponent.LowerBusbars);

  const spouts = [SubLocation.SUB_LOC_LEFT, SubLocation.SUB_LOC_CENTRE, SubLocation.SUB_LOC_RIGHT].map(
    (spoutSublocation) => buildSubstationLocationDetails(MeasurementComponent.Spouts, spoutSublocation),
  );

  const circuitBreaker = buildSubstationLocationDetails(MeasurementComponent.CircuitBreaker);

  return [...cables, ...cableBox, ctChamber, vtChamber, upperBusbars, lowerBusbars, ...spouts, circuitBreaker];
}

interface SubstationLocationDetails {
  label: string;
  component: MeasurementComponent;
  subLocation: SubLocation;
}

function buildSubstationLocationDetails(
  component: MeasurementComponent,
  sublocation?: SubLocation,
  cableNaming?: CableNaming,
) {
  const componentLabel = component !== MeasurementComponent.Cables ? I18n.t(`MeasurementComponents.${component}`) : "";
  const sublocationLabel = sublocation ? getTranslatedSubLocationName(sublocation, component, cableNaming) : "";

  return {
    label: `${componentLabel} ${sublocationLabel}`,
    component: component,
    subLocation: sublocation || null,
  } as SubstationLocationDetails;
}
